import * as React from "react";
import { observer } from "mobx-react";
import {
	Wrapper,
	Content,
	Table,
	TableHead,
	TableHeadRow,
	TableHeadCell,
	TableBody,
	TableCell,
	TableCellAlert,
	TableRow,
	Loader,
	ScrollX,
	Inner,
} from "@lib/components/src/components/table/pro/components";
import { ProtableTopBar } from "@lib/components/src/components/table/pro/top";
import { ProtableControls } from "@lib/components/src/components/table/pro/controls";
import { BookingsProtableFilters } from "./bookings-protable-filters";
import { BookingsProtableExport } from "./bookings-protable-export";
import { RotateLoader, Checkbox } from "@lib/components";
import { FaExclamation } from "react-icons/fa";
import { BookingsTableStore, BookingsTableFiltersState } from "./bookings-protable-store";
import styled from "styled-components";

/** Matches `Inner` left gutter so filter/export panels align with the table. */
const PanelGutter = styled.div`
	padding-left: 12px;
	box-sizing: border-box;
`;

interface Column<T extends { _id: string }> {
	title: React.ReactNode;
	breakpoint?: number;
	className?: string;
	width?: number;
	render: (row: T) => React.ReactNode;
	hide?: boolean;
}

interface FilterConfigs {
	noSort?: boolean;
	noSearch?: boolean;
}

interface Props<T extends { _id: string }> {
	items: T[];
	count: number;
	screenWidth: number;
	query: T.Lib.Table.Query;
	columns: Array<Column<T>>;
	rowAlert?: (row: T) => boolean;
	fetch: T.Lib.Table.FetchFunction;
	onClick: (row: T) => void;
	exportUrl: string;
	onPageChange?: (page: number) => void;
	region: T.Schema.Restaurant.RestaurantSchema["settings"]["region"];
	restaurantId: string;
	persistedFilters?: BookingsTableFiltersState;
	onPersistFilters?: (state: BookingsTableFiltersState) => void;
	selectedRows?: string[];
	onChangeRowSelect?: (selected: string[]) => void;
	exportSettings?: T.Schema.Restaurant.RestaurantSettings["order_export"];
	openCustomExportModal?: () => void;
	filterConfigs?: FilterConfigs;
}

/** Bookings-only table: same UI as Protable but defaults sort to booking date. */
@observer
export class BookingsProtable<T extends { _id: string; status: string }> extends React.Component<
	Props<T>
> {
	store: BookingsTableStore<T>;

	constructor(props: Props<T>) {
		super(props);
		this.state = {};
		this.store = new BookingsTableStore(props.fetch, props.region.timezone, {
			initial: props.persistedFilters,
			onPersist: props.onPersistFilters,
		});
		this.store.update({
			items: props.items,
			count: props.count,
		}, { skipPersist: true });
	}

	componentDidMount() {
		this.store.fetch();
	}

	componentDidUpdate() {
		this.store.update({
			items: this.props.items,
			count: this.props.count,
		}, { skipPersist: true });
	}

	render() {
		const {
			screenWidth,
			columns,
			query,
			onClick,
			exportUrl,
			rowAlert,
			onPageChange,
			region,
			onChangeRowSelect,
			selectedRows,
			exportSettings,
			openCustomExportModal,
			filterConfigs,
		} = this.props;

		const { items, loading, error, dropdown } = this.store.s;
		const showExportButton = !!exportUrl;
		const showExportModule = dropdown === "export";

		return (
			<Wrapper>
				<PanelGutter>
					<Content>
						<BookingsProtableFilters<T>
							store={this.store}
							query={query}
							region={region}
						/>

						<BookingsProtableExport<T>
							store={this.store}
							exportUrl={exportUrl}
							region={region}
						/>
					</Content>
				</PanelGutter>

				{!showExportModule && (
					<>
						<ScrollX>
							<Inner>
								<Content>
									<ProtableTopBar<T>
										store={this.store}
										showExportModule={showExportButton}
									/>
									<Table>
										<TableHead>
											<TableHeadRow>
												{!!onChangeRowSelect && (
													<TableHeadCell className={""} />
												)}
												{columns.map((c, i) => {
													if (c.hide) return null;
													if (
														c.breakpoint !== undefined &&
														screenWidth < c.breakpoint
													)
														return null;
													return (
														<TableHeadCell
															key={i}
															className={c.className || ""}
														>
															{c.title}
														</TableHeadCell>
													);
												})}
											</TableHeadRow>
										</TableHead>
										<TableBody>
											{!loading &&
												!error &&
												items
													.filter((item) => item.status !== "draft")
													.map((item, i) => {
														const selectedIndex = (
															selectedRows || []
														).indexOf(item._id);
														return (
															<TableRow
																key={i}
																onClick={() => onClick(item)}
															>
																{!!onChangeRowSelect && (
																	<TableCell
																		className="p-l-3 p-r-3"
																		style={{ width: "42px" }}
																		onClick={(e) => {
																			e.stopPropagation();
																			const selected = [
																				...(selectedRows || []),
																			];
																			if (selectedIndex !== -1) {
																				selected.splice(
																					selectedIndex,
																					1
																				);
																			} else {
																				selected.push(item._id);
																			}
																			onChangeRowSelect(selected);
																		}}
																	>
																		{rowAlert && rowAlert(item) && (
																			<TableCellAlert>
																				<FaExclamation />
																			</TableCellAlert>
																		)}
																		<Checkbox
																			id={`select-${item._id}`}
																			checked={selectedIndex !== -1}
																		/>
																	</TableCell>
																)}
																{columns.map((c, k) => {
																	if (c.hide) return null;
																	if (
																		c.breakpoint !== undefined &&
																		screenWidth < c.breakpoint
																	)
																		return null;
																	return (
																		<TableCell
																			key={k}
																			style={{
																				width: c.width
																					? `${c.width}px`
																					: "auto",
																			}}
																		>
																			{!onChangeRowSelect &&
																				k === 0 &&
																				rowAlert &&
																				rowAlert(item) && (
																					<TableCellAlert>
																						<FaExclamation />
																					</TableCellAlert>
																				)}
																			{c.render(item)}
																		</TableCell>
																	);
																})}
															</TableRow>
														);
													})}
										</TableBody>
									</Table>

									{loading && (
										<Loader>
											<RotateLoader size={4} className="center" />
										</Loader>
									)}

									{!loading && !error && items.length === 0 && (
										<Loader>
											<p className="text-center big">No Items Found</p>
										</Loader>
									)}

									{!loading && error && (
										<Loader>
											<p className="text-center error-text big">{error}</p>
										</Loader>
									)}
								</Content>
							</Inner>
						</ScrollX>
						<ProtableControls<T>
							store={this.store}
							onPageChange={onPageChange}
						/>

						<p className="small text-center m-t-2 font-semi-bold">Page</p>
					</>
				)}
			</Wrapper>
		);
	}
}
